import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { ContactButton } from '../components/common/ContactButton'

export function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            <strong>Effective Date:</strong> August 1, 2025<br />
            <strong>Last Updated:</strong> August 1, 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using CYARoutes ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              CYARoutes is a platform for creating, sharing, and discovering cycling routes. We provide tools for route planning, community interaction, and cycling club management.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Registration</h3>
            <p className="text-gray-700 mb-4">
              To access certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Age Requirements</h3>
            <p className="text-gray-700 mb-4">
              You must be at least 16 years old to use our Service. By using the Service, you represent that you meet this age requirement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Content</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Your Content</h3>
            <p className="text-gray-700 mb-4">
              You retain ownership of content you create ("User Content"), including routes, photos, and comments. By sharing content, you grant us a worldwide, non-exclusive, royalty-free license to use, display, and distribute your content in connection with the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Content Standards</h3>
            <p className="text-gray-700 mb-4">You agree not to post content that:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Violates any laws or regulations</li>
              <li>Infringes on intellectual property rights</li>
              <li>Contains harmful, offensive, or discriminatory material</li>
              <li>Includes spam or unauthorized advertising</li>
              <li>Misrepresents routes or contains dangerous information</li>
              <li>Violates the privacy of others</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Content Removal</h3>
            <p className="text-gray-700 mb-4">
              We reserve the right to remove any content that violates these Terms or is otherwise objectionable, without prior notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Scrape or harvest data without permission</li>
              <li>Impersonate others or create false accounts</li>
              <li>Use automated systems or bots without authorization</li>
              <li>Circumvent any security measures</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Safety and Liability</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Assumption of Risk</h3>
            <p className="text-gray-700 mb-4">
              <strong className="text-red-600">IMPORTANT:</strong> Cycling involves inherent risks. Routes on our platform are user-generated and not professionally verified. You acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>You use routes at your own risk</li>
              <li>You are responsible for assessing route safety and your abilities</li>
              <li>Conditions may have changed since route creation</li>
              <li>You should always follow local laws and regulations</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 No Warranty</h3>
            <p className="text-gray-700 mb-4">
              We do not warrant the accuracy, completeness, or safety of any routes or information on our platform. Always use your own judgment and verify information independently.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.3 Navigation Disclaimer</h3>
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
              <p className="text-gray-700 mb-2">
                <strong className="text-red-600">TURN-BY-TURN NAVIGATION:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Turn-by-turn navigation is provided as guidance only</li>
                <li>Always prioritize traffic signals, signs, and road rules over app guidance</li>
                <li>Do not interact with the app while riding</li>
                <li>Navigation may continue in background - ensure device is securely mounted</li>
                <li>GPS accuracy may vary; always verify your location</li>
                <li>You are solely responsible for your safety while cycling</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.4 Hazard Reporting</h3>
            <p className="text-gray-700 mb-2">
              Regarding the hazard reporting feature:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Hazard reports are user-generated and not verified by us</li>
              <li>Report accuracy and timeliness are not guaranteed</li>
              <li>Do not rely solely on hazard reports for safety decisions</li>
              <li>You agree to submit accurate hazard information</li>
              <li>We reserve the right to moderate or remove hazard reports</li>
              <li>False or malicious reports may result in account suspension</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              The Service, including its design, features, and content (excluding User Content), is owned by us and protected by intellectual property laws. You may not copy, modify, or reverse engineer any part of the Service without permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              Our Service integrates with the following third-party services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Mapbox & Google Places:</strong> Map display, routing, and location services</li>
              <li><strong>GraphHopper:</strong> Route planning and navigation calculations</li>
              <li><strong>Strava:</strong> Optional activity sync and route coverage tracking (requires separate authentication)</li>
              <li><strong>Firebase:</strong> Authentication, data storage, and crash reporting</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Your use of these services is subject to their respective terms and policies. We are not responsible for third-party service availability, accuracy, or changes to their services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
              <p className="text-gray-700 font-semibold">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
              <p className="text-gray-700 font-semibold">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO PERSONAL INJURY, PROPERTY DAMAGE, LOST PROFITS, OR DAMAGES RESULTING FROM USE OF THE SERVICE OR ROUTES.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-700 mb-4">
              You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the Service, your content, or your violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Termination</h2>
            <p className="text-gray-700 mb-4">
              We may terminate or suspend your account at any time for violation of these Terms. You may delete your account at any time through your account settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We may modify these Terms at any time. We will notify users of material changes. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms are governed by the laws of Victoria, Australia, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Victoria, Australia.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Severability</h2>
            <p className="text-gray-700 mb-4">
              If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms, please contact us at:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="text-gray-700">
                <strong>Contact:</strong> <ContactButton subject="Terms of Service Inquiry">Send us a message</ContactButton><br />
                <strong>Address:</strong> Melbourne, Australia
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Mobile Application Terms (iOS)</h2>
            <p className="text-gray-700 mb-4">
              If you access our Service through the iOS mobile application:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>The app is distributed via the Apple App Store and is subject to Apple's Licensed Application End User License Agreement (EULA)</li>
              <li>Apple Inc. and its subsidiaries are third-party beneficiaries of these Terms and have the right to enforce these Terms against you</li>
              <li>Apple is not responsible for the app, its content, maintenance, or support services</li>
              <li>Apple has no warranty obligations with respect to the app</li>
              <li>Apple is not responsible for addressing any claims by you or third parties relating to the app</li>
              <li>In the event of any third-party claim that the app infringes intellectual property rights, we (not Apple) are responsible for investigation and defense</li>
            </ul>
          </section>

          <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-gray-700 text-sm">
              <strong>Note:</strong> By using CYARoutes, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}